import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { trackEvent } from "@/lib/analytics";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import {
  ArrowLeft, ArrowRight, Check, Loader2, Sparkles, Building2, Users,
  Wrench, HandshakeIcon, Rocket, PartyPopper, ClipboardList, Handshake,
} from "lucide-react";

const FIELDS_OF_WORK = [
  "خدمات اجتماعية/أسرية","تمكين اقتصادي","صحة","تعليم","بيئة","ثقافة/تراث","شباب/رياضة","ضيوف الرحمن/خدمات حج وعمرة","أخرى",
];
const SIZES = ["أقل من 10 موظفين","10–50","أكثر من 50","لا يوجد موظفون"];
const POSITIONS = [
  "إدارة مشاريع","جمع التبرعات/تنمية الموارد","التواصل والإعلام","إدارة المتطوعين",
  "بحوث ودراسات","تحليل بيانات","مسؤول قياس أثر","ابتكار خدمات","مسؤول الاستثمار الاجتماعي",
  "أخصائي الإسناد الحكومي","تشغيل ميداني","محاسبة/مالية","قانوني/حوكمة","تقنية معلومات/منصات","أخرى",
];
const DURATIONS = ["4–6 أسابيع","8–12 أسبوعاً","3–6 أشهر","أخرى"];
const MODES = ["حضوري","عن بُعد","هجين"];
const OBSTACLES = [
  "عدم توفر مشرف","ضغط العمل الميداني","ضعف البنية التقنية","عدم وضوح أدوار المتدرب",
  "تحديات لوجستية/مكانية","مخاوف تتعلق بالسرية والبيانات","أخرى",
];
const PARTNERSHIP_MODELS = [
  "استقبال متدربين فقط",
  "استقبال ومشاركة في تصميم المهام",
  "استقبال وإشراف مشترك مع الجامعة",
  "استقبال مع فرصة توظيف/استقطاب لاحق",
];
const FOLLOW_UPS = [
  "اجتماعات أسبوعية","تقارير مرحلية","منصة رقمية للمتابعة","تقييم منتصف ونهاية التدريب التعاوني","أخرى",
];

const CURRENT_YEAR = new Date().getFullYear();

const STEP_META = [
  { title: "أهلاً بجمعيتكم في عماد", subtitle: "أسئلة قصيرة تُخصّص شراكتنا معكم", icon: Sparkles, hue: "from-teal-700 to-teal-900" },
  { title: "التعريف بالجمعية", subtitle: "مَن أنتم؟", icon: Building2, hue: "from-sky-700 to-teal-800" },
  { title: "الاحتياج من الكفاءات", subtitle: "أين الفجوة اليوم؟", icon: Wrench, hue: "from-amber-700 to-orange-800" },
  { title: "جاهزية الاستقبال", subtitle: "قدرتكم على استضافة المتدربين", icon: Users, hue: "from-emerald-700 to-teal-800" },
  { title: "التجارب السابقة (اختياري)", subtitle: "ما الذي نتعلّم منه؟", icon: ClipboardList, hue: "from-purple-700 to-indigo-800" },
  { title: "نموذج الشراكة والمتابعة", subtitle: "كيف نعمل معاً؟", icon: Handshake, hue: "from-rose-700 to-red-800" },
  { title: "شراكة انطلقت!", subtitle: "شكراً — سنتواصل معكم", icon: PartyPopper, hue: "from-teal-600 to-emerald-700" },
];

export default function AssociationOnboarding() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState(() => {
    const cached = localStorage.getItem("emad_assoc_survey_draft");
    return cached ? JSON.parse(cached) : { org_name: user?.organization_name || user?.name || "" };
  });
  useEffect(() => { localStorage.setItem("emad_assoc_survey_draft", JSON.stringify(values)); }, [values]);

  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const validators = useMemo(() => [
    () => true,
    () => {
      const foundedYear = Number(values.founded_year);
      return values.org_name && values.field_of_work && (values.field_of_work !== "أخرى" || values.field_of_work_other)
        && values.city && /^\d{4}$/.test(String(values.founded_year || ""))
        && foundedYear >= 1800 && foundedYear <= CURRENT_YEAR && values.size && values.has_volunteers;
    },
    () => values.impact_rating >= 1 && (values.needed_positions?.length > 0),
    () => values.can_host_trainees && values.preferred_duration && (values.preferred_duration !== "أخرى" || values.preferred_duration_other) && values.preferred_mode && (values.obstacles?.length > 0),
    () => true, // optional section
    () => (values.partnership_model?.length > 0) && values.follow_up_method && (values.follow_up_method !== "أخرى" || values.follow_up_method_other),
    () => true,
  ], [values]);
  const canNext = validators[step]?.();

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/association/survey", values);
      trackEvent("onboarding_completed", { role: "association" });
      localStorage.removeItem("emad_assoc_survey_draft");
      toast.success("تم استلام استبيان الجمعية");
      if (refresh) await refresh();
      setStep(STEP_META.length - 1);
      setTimeout(() => navigate("/dashboard", { replace: true }), 1800);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const meta = STEP_META[step];
  const Icon = meta.icon;
  const progress = Math.round((step / (STEP_META.length - 1)) * 100);

  return (
    <div className="min-h-screen bg-cream-50" dir="rtl" data-testid="assoc-onboarding-page">
      <div className={`bg-gradient-to-l ${meta.hue} text-white`}>
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Logo variant="cream" size={80} />
          <div className="text-xs font-semibold opacity-90 arabic-numerals">الخطوة {step + 1} من {STEP_META.length}</div>
        </div>
        <div className="h-1.5 bg-black/20"><div className="h-full bg-white/90 transition-all duration-500" style={{ width: `${progress}%` }} /></div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="mb-8 flex items-start gap-4">
          <div className={`h-14 w-14 rounded-2xl bg-gradient-to-br ${meta.hue} text-white grid place-items-center shrink-0`}><Icon className="h-7 w-7" /></div>
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-teal-700">رحلة الجمعية</div>
            <h1 className="mt-1 text-2xl font-heading font-black text-slate-900">{meta.title}</h1>
            <p className="mt-1 text-slate-600">{meta.subtitle}</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-cream-200 card-shadow p-4 md:p-8" data-testid={`assoc-step-${step}`}>
          {step === 0 && <Intro user={user} onStart={() => setStep(1)} />}
          {step === 1 && <SectionAbout values={values} set={set} />}
          {step === 2 && <SectionNeeds values={values} set={set} />}
          {step === 3 && <SectionReadiness values={values} set={set} />}
          {step === 4 && <SectionPrior values={values} set={set} />}
          {step === 5 && <SectionPartnership values={values} set={set} />}
          {step === 6 && <Done />}
        </div>

        {step > 0 && step < 6 && (
          <div className="mt-6 flex items-center justify-between gap-3">
            <button onClick={() => setStep((s) => Math.max(0, s - 1))} className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm font-semibold" data-testid="assoc-prev"><ArrowRight className="h-4 w-4" /> السابق</button>
            {step < 5 ? (
              <button onClick={() => canNext && setStep((s) => s + 1)} disabled={!canNext} className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-teal-700 text-white hover:bg-teal-600 disabled:opacity-50 text-sm font-semibold" data-testid="assoc-next">التالي <ArrowLeft className="h-4 w-4" /></button>
            ) : (
              <button onClick={submit} disabled={!canNext || saving} className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-teal-700 text-white hover:bg-teal-600 disabled:opacity-60 text-sm font-semibold" data-testid="assoc-submit">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />} إرسال الاستبيان
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Intro({ user, onStart }) {
  return (
    <div className="space-y-5">
      <p className="text-lg text-slate-800 leading-relaxed">
        أهلاً بكم <strong className="text-teal-700">{user?.organization_name || user?.name || "أعزّاءنا"}</strong> 👋<br/>
        قبل أن نبدأ شراكتنا، نودّ أن نتعرّف على جمعيتكم وطموحاتكم كي نُخصّص الطلاب والبرامج الأنسب لكم. مدة تعبئة الاستبيان لا تتجاوز <strong>5 دقائق</strong>.
      </p>
      <ul className="space-y-2 text-sm text-slate-600">
        <li className="flex items-center gap-2"><Check className="h-4 w-4 text-teal-700"/> إجاباتكم تُحفظ تلقائياً حتى لو أغلقتم الصفحة</li>
        <li className="flex items-center gap-2"><Check className="h-4 w-4 text-teal-700"/> قسم «التجارب السابقة» اختياري تماماً</li>
        <li className="flex items-center gap-2"><Check className="h-4 w-4 text-teal-700"/> نستخدم إجاباتكم لترشيح الطلاب المتوافقين مع احتياجاتكم</li>
      </ul>
      <button onClick={onStart} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg bg-teal-700 text-white text-base font-semibold hover:bg-teal-600" data-testid="assoc-start">هيا نبدأ <ArrowLeft className="h-5 w-5" /></button>
    </div>
  );
}

function SectionAbout({ values, set }) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Text label="اسم الجمعية" value={values.org_name} onChange={(v) => set("org_name", v)} testid="q-org-name" />
      <div className="md:col-span-2">
        <Select label="مجال عمل الجمعية" options={FIELDS_OF_WORK} value={values.field_of_work} onChange={(v) => set("field_of_work", v)} testid="q-field" />
        {values.field_of_work === "أخرى" && (
          <input autoFocus value={values.field_of_work_other || ""} onChange={(e) => set("field_of_work_other", e.target.value)} placeholder="اذكر المجال" className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none" data-testid="q-field-other" />
        )}
      </div>
      <Text label="المدينة" value={values.city} onChange={(v) => set("city", v)} testid="q-city" />
      <Text label="سنة التأسيس" type="number" dir="ltr" min="1800" max={CURRENT_YEAR} inputMode="numeric" placeholder="مثال: 2015" value={values.founded_year} onChange={(v) => set("founded_year", v)} testid="q-founded" />
      <div className="md:col-span-2">
        <RadioGroup label="حجم الجمعية" options={SIZES} value={values.size} onChange={(v) => set("size", v)} name="size" />
      </div>
      <div className="md:col-span-2">
        <RadioGroup label="هل لديكم متطوعون نشطون؟" options={["نعم","لا"]} value={values.has_volunteers} onChange={(v) => set("has_volunteers", v)} name="volunteers" />
      </div>
    </div>
  );
}

function SectionNeeds({ values, set }) {
  return (
    <div className="grid gap-5">
      <Rating label="درجة تأثر أداء الجمعية بنقص الكفاءات (1 غير متأثر — 5 متأثر بشدة)" min={1} max={5} value={values.impact_rating} onChange={(v) => set("impact_rating", v)} testid="q-impact" />
      <div>
        <CheckboxGroup label="ما أكثر الوظائف نقصاً لديكم؟ (يمكن اختيار عدة خيارات)" options={POSITIONS} value={values.needed_positions || []} onChange={(v) => set("needed_positions", v)} name="positions" />
        {values.needed_positions?.includes("أخرى") && (
          <input autoFocus value={values.needed_positions_other || ""} onChange={(e) => set("needed_positions_other", e.target.value)} placeholder="اذكر وظائف أخرى" className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none" data-testid="q-positions-other" />
        )}
      </div>
    </div>
  );
}

function SectionReadiness({ values, set }) {
  return (
    <div className="grid gap-5">
      <RadioGroup label="هل لديكم قدرة على استقبال طلاب تدريب تعاوني خلال العام؟" options={["نعم","لا"]} value={values.can_host_trainees} onChange={(v) => set("can_host_trainees", v)} name="can_host" />
      <div>
        <RadioGroup label="المدة الأنسب للتدريب التعاوني" options={DURATIONS} value={values.preferred_duration} onChange={(v) => set("preferred_duration", v)} name="dur" />
        {values.preferred_duration === "أخرى" && (
          <input autoFocus value={values.preferred_duration_other || ""} onChange={(e) => set("preferred_duration_other", e.target.value)} placeholder="اذكر المدة" className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none" data-testid="q-duration-other" />
        )}
      </div>
      <RadioGroup label="نمط التدريب المفضل" options={MODES} value={values.preferred_mode} onChange={(v) => set("preferred_mode", v)} name="mode" />
      <div>
        <CheckboxGroup label="ما أبرز العوائق؟" options={OBSTACLES} value={values.obstacles || []} onChange={(v) => set("obstacles", v)} name="obstacles" />
        {values.obstacles?.includes("أخرى") && (
          <input autoFocus value={values.obstacles_other || ""} onChange={(e) => set("obstacles_other", e.target.value)} placeholder="اذكر عائقاً آخر" className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none" data-testid="q-obstacles-other" />
        )}
      </div>
    </div>
  );
}

function SectionPrior({ values, set }) {
  return (
    <div className="grid gap-5">
      <div className="p-3 rounded-lg bg-cream-50 border border-cream-200 text-sm text-slate-600">هذا القسم <strong>اختياري تماماً</strong> — يمكنكم تخطّيه.</div>
      <RadioGroup label="هل سبق استضافة متدربين؟" options={["نعم","لا"]} value={values.hosted_before} onChange={(v) => set("hosted_before", v)} name="hosted" />
      {values.hosted_before === "نعم" && (
        <>
          <Rating label="تقييم التجربة السابقة (1 ضعيف — 5 ممتاز)" min={1} max={5} value={values.prior_rating} onChange={(v) => set("prior_rating", v)} testid="q-prior-rating" />
          <Text label="ما الذي تطمحون لتغييره لتحسين التجربة القادمة؟" multiline value={values.improvement_notes} onChange={(v) => set("improvement_notes", v)} testid="q-improvement" />
        </>
      )}
    </div>
  );
}

function SectionPartnership({ values, set }) {
  return (
    <div className="grid gap-5">
      <CheckboxGroup label="نموذج الشراكة المفضل (يمكن اختيار عدة خيارات)" options={PARTNERSHIP_MODELS} value={Array.isArray(values.partnership_model) ? values.partnership_model : (values.partnership_model ? [values.partnership_model] : [])} onChange={(v) => set("partnership_model", v)} name="partner" />
      <div>
        <CheckboxGroup label="آلية المتابعة الأنسب (يمكن اختيار عدة خيارات)" options={FOLLOW_UPS} value={Array.isArray(values.follow_up_method) ? values.follow_up_method : (values.follow_up_method ? [values.follow_up_method] : [])} onChange={(v) => set("follow_up_method", v)} name="followup" />
        {(Array.isArray(values.follow_up_method) ? values.follow_up_method : []).includes("أخرى") && (
          <input autoFocus value={values.follow_up_method_other || ""} onChange={(e) => set("follow_up_method_other", e.target.value)} placeholder="اذكر آلية أخرى" className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none" data-testid="q-followup-other" />
        )}
      </div>
    </div>
  );
}

function Done() {
  return (
    <div className="text-center py-8">
      <div className="mx-auto h-16 w-16 rounded-full bg-teal-100 grid place-items-center mb-4"><PartyPopper className="h-9 w-9 text-teal-700" /></div>
      <h2 className="text-2xl font-heading font-black text-slate-900">شراكة انطلقت! 🎉</h2>
      <p className="mt-2 text-slate-600">ننقلكم الآن إلى لوحة تحكم الجمعية...</p>
    </div>
  );
}

// -------- Field components (shared, kept local for simplicity) --------
function Text({ label, value, onChange, type = "text", dir, multiline, testid, min, max, inputMode, placeholder }) {
  const Comp = multiline ? "textarea" : "input";
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 mb-1.5">{label}</span>
      <Comp type={type} rows={multiline ? 3 : undefined} dir={dir} min={min} max={max} inputMode={inputMode} placeholder={placeholder} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none" data-testid={testid} />
    </label>
  );
}
function Select({ label, options, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 mb-1.5">{label}</span>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none" data-testid={testid}>
        <option value="">— اختر —</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
function RadioGroup({ label, options, value, onChange, name }) {
  return (
    <div>
      <div className="text-sm font-medium text-slate-700 mb-2">{label}</div>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button key={o} type="button" onClick={() => onChange(o)} className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${value === o ? "bg-teal-700 text-white border-teal-700" : "bg-white text-slate-700 border-slate-200 hover:border-teal-500"}`} data-testid={`q-${name}-${o}`}>{o}</button>
        ))}
      </div>
    </div>
  );
}
function CheckboxGroup({ label, options, value, onChange, name }) {
  const toggle = (o) => onChange(value.includes(o) ? value.filter((x) => x !== o) : [...value, o]);
  return (
    <div>
      <div className="text-sm font-medium text-slate-700 mb-2">{label}</div>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          const active = value.includes(o);
          return (
            <button key={o} type="button" onClick={() => toggle(o)} className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${active ? "bg-teal-700 text-white border-teal-700" : "bg-white text-slate-700 border-slate-200 hover:border-teal-500"}`} data-testid={`q-${name}-${o}`}>
              {active && <Check className="inline h-3.5 w-3.5 ms-1" />}{o}
            </button>
          );
        })}
      </div>
    </div>
  );
}
function Rating({ label, min = 1, max = 5, value, onChange, testid }) {
  return (
    <div>
      <div className="text-sm font-medium text-slate-700 mb-2">{label}</div>
      <div className="flex items-center gap-1.5" data-testid={testid}>
        {Array.from({ length: max - min + 1 }, (_, i) => i + min).map((n) => (
          <button key={n} type="button" onClick={() => onChange(n)} className={`h-10 w-10 rounded-full text-xs font-bold border transition-all arabic-numerals ${value === n ? "bg-amber-500 text-white border-amber-500" : "bg-white text-slate-500 border-slate-200 hover:border-amber-400"}`} data-testid={`${testid}-${n}`}>{n}</button>
        ))}
      </div>
    </div>
  );
}
